import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Redirect if already logged in
  if (isAuthenticated) {
    navigate("/", { replace: true });
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center relative overflow-hidden">
      {/* Ambient radar circles — very dim */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[600px] h-[600px] rounded-full border border-[rgba(255,255,255,0.02)]" />
        <div className="absolute w-[450px] h-[450px] rounded-full border border-[rgba(255,255,255,0.03)]" />
        <div className="absolute w-[300px] h-[300px] rounded-full border border-[rgba(255,255,255,0.04)]" />
        <div className="absolute w-[150px] h-[150px] rounded-full border border-[rgba(255,255,255,0.05)]" />
      </div>

      {/* Subtle radial glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(ellipse at center, rgba(75, 117, 242, 0.03) 0%, transparent 60%)",
        }}
      />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center w-full max-w-[360px] px-4">
        {/* Brand title */}
        <div className="flex flex-col items-center mb-2">
          <div className="relative">
            <h1 className="font-brand text-[56px] leading-none tracking-[0.02em] text-foreground">
              STRANGELOVE
              <span className="text-electric-blue"> OS</span>
            </h1>
            {/* Reflection effect */}
            <div
              className="font-brand text-[56px] leading-none tracking-[0.02em] text-foreground/10 select-none"
              style={{
                transform: "scaleY(-1)",
                maskImage: "linear-gradient(to bottom, rgba(0,0,0,0.12), transparent 50%)",
                WebkitMaskImage: "linear-gradient(to bottom, rgba(0,0,0,0.12), transparent 50%)",
              }}
              aria-hidden
            >
              STRANGELOVE
              <span className="text-electric-blue/10"> OS</span>
            </div>
          </div>
        </div>

        {/* Tagline */}
        <p className="font-brand text-base tracking-[0.1em] text-muted-foreground mb-8">
          HOW I LEARNED TO STOP WORRYING AND LOVE THE BUILD
        </p>

        {/* Login form — glass card */}
        <form onSubmit={handleSubmit} className="w-full flex flex-col gap-3">
          {error && (
            <div className="text-[#CC2936] text-sm text-center bg-[rgba(204,41,54,0.1)] border border-[rgba(204,41,54,0.2)] rounded-md px-3 py-2">
              {error}
            </div>
          )}
          <Input
            type="email"
            placeholder="commander@digitalempire.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-11 bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.08)] text-foreground placeholder:text-[#555] focus:border-electric-blue focus:ring-electric-blue/20"
            autoComplete="email"
          />
          <Input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="h-11 bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.08)] text-foreground placeholder:text-[#555] focus:border-electric-blue focus:ring-electric-blue/20"
            autoComplete="current-password"
          />
          <Button
            type="submit"
            disabled={loading}
            className="h-11 w-full bg-electric-blue hover:bg-electric-blue/90 text-white font-semibold text-sm uppercase tracking-wider rounded-[6px] mt-1"
          >
            {loading ? "ENTERING..." : "ENTER THE WAR ROOM"}
          </Button>
        </form>

        {/* Footer */}
        <p className="mt-8 text-[11px] text-[#555]">
          Digital Empire Marketing LLC
        </p>
      </div>
    </div>
  );
}
